'use client'

import { useState, type FC } from 'react'
import IconSelector from 'components/AdminComponents/Config/IconSelector'
import { IconCheckmark, IconDelete, IconDuplicate } from 'components/icons'
import SelectMenu from 'components/SelectMenu'
import { Button } from 'components/ui/button'
import { Checkbox } from 'components/ui/checkbox'
import { Label } from 'ui/label'
import { Textarea } from 'components/ui/textarea'
import { Input } from 'ui/input'
import { ChevronRight, Target } from 'lucide-react'
import FormUploadImage from '../FormUploadImage'
import FieldWrapper from './FieldWrapper'
import type { FieldRendererProps } from './types'

/** Default shape for a new cardArray item. */
export const CARD_ARRAY_DEFAULT = {
    title: '',
    icon: '',
    iconPlacement: 'normal',
    description: '',
    showNumber: false,
    image: '',
    link: '',
    buttons: [] as { text?: string; link?: string; variant?: string; icon?: string }[]
} as const

export const cardArrayFieldConfig = {
    getInitialValue: (field: TFormField) => field.defaultValue ?? [CARD_ARRAY_DEFAULT],
    getNewArrayItem: () => ({ ...CARD_ARRAY_DEFAULT }),
    cloneForDuplicate: (_field: TFormField, item: unknown) => {
        const copy = typeof item === 'object' && item !== null ? { ...(item as object) } : item
        if (copy && typeof copy === 'object' && 'buttons' in copy) {
            const c = copy as { buttons?: unknown[] }
            if (Array.isArray(c.buttons)) {
                c.buttons = c.buttons.map((b: unknown) =>
                    typeof b === 'object' && b !== null ? { ...(b as object) } : b
                )
            }
        }
        return copy
    },
    extraPropsKind: 'array' as const
} satisfies Partial<import('./types').FieldTypeConfig>

type CardItem = {
    title?: string
    icon?: string
    iconPlacement?: string
    description?: string
    showNumber?: boolean
    image?: string | File
    link?: string
    buttons?: { text?: string; link?: string; variant?: string; icon?: string }[]
}

const CardArrayItem = ({
    item,
    index,
    cardTitle,
    hasContent,
    field,
    handleArrayChange,
    handleDuplicateArrayItem,
    handleRemoveArrayItem,
    formState,
    isInitiallyExpanded
}: {
    item: CardItem
    index: number
    cardTitle: string
    hasContent: boolean
    field: TFormField
    handleArrayChange: (n: string, i: number, f: TFormField, v: CardItem) => void
    handleDuplicateArrayItem: (n: string, i: number, f: TFormField) => void
    handleRemoveArrayItem: (n: string, i: number, f: TFormField) => void
    formState: { [key: string]: unknown }
    isInitiallyExpanded: boolean
}) => {
    const [isExpanded, setIsExpanded] = useState(isInitiallyExpanded)

    return (
        <div className='border border-border rounded-lg bg-card overflow-hidden'>
            <div
                className={`p-3 ${isExpanded ? 'border-b' : ''} flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors`}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className='flex items-center gap-2'>
                    <Button variant='ghost' size='sm' type='button' className='h-6 w-6 p-0'>
                        <ChevronRight
                            className={`h-4 w-4 transition-transform stroke-2 ${isExpanded ? 'rotate-90' : ''}`}
                        />
                    </Button>
                    <div className='flex items-center gap-2'>
                        {item.icon && (
                            <div className='w-6 h-6 bg-accent rounded-full flex items-center justify-center'>
                                <Target className='h-3 w-3 text-accent-foreground' />
                            </div>
                        )}
                        <div>
                            <h4 className='text-sm font-medium'>{cardTitle}</h4>
                            {item.description && (
                                <p className='text-xs text-muted-foreground truncate max-w-[120px]'>
                                    {item.description}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className='flex items-center gap-1 text-xs'>
                        {hasContent && <IconCheckmark className='text-success' />}
                        {(item.buttons?.length ?? 0) > 0 && (
                            <span className='bg-secondary flex text-foreground/80 flex-nowrap whitespace-nowrap px-2 py-1 rounded-full'>
                                {item.buttons!.length} button{item.buttons!.length > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                </div>
                <div className='flex gap-1'>
                    <Button
                        variant='ghost'
                        size='icon'
                        type='button'
                        onClick={e => {
                            e.stopPropagation()
                            handleDuplicateArrayItem(field.name, index, field)
                        }}
                        disabled={
                            !!field.max &&
                            (formState[field.name] as CardItem[])?.length >= field.max
                        }
                        className='size-6'
                        title='Duplicate card'
                    >
                        <IconDuplicate />
                    </Button>
                    <Button
                        variant='ghost'
                        size='icon'
                        type='button'
                        onClick={e => {
                            e.stopPropagation()
                            handleRemoveArrayItem(field.name, index, field)
                        }}
                        disabled={
                            !!field.min &&
                            (formState[field.name] as CardItem[])?.length <= field.min
                        }
                        className='size-6'
                        title='Delete card'
                    >
                        <IconDelete />
                    </Button>
                </div>
            </div>

            {isExpanded && (
                <div className='p-4 space-y-4'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label className='text-xs font-medium'>Title</Label>
                            <Input
                                placeholder='Card title'
                                value={item.title || ''}
                                onChange={e =>
                                    handleArrayChange(field.name, index, field, {
                                        ...item,
                                        title: e.target.value
                                    })
                                }
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label className='text-xs font-medium'>Link (optional)</Label>
                            <Input
                                placeholder='Card link'
                                value={item.link || ''}
                                onChange={e =>
                                    handleArrayChange(field.name, index, field, {
                                        ...item,
                                        link: e.target.value
                                    })
                                }
                            />
                        </div>
                    </div>

                    <div className='space-y-2'>
                        <Label className='text-xs font-medium'>Description</Label>
                        <Textarea
                            placeholder='Card description'
                            className='resize-none'
                            value={item.description || ''}
                            onChange={e =>
                                handleArrayChange(field.name, index, field, {
                                    ...item,
                                    description: e.target.value
                                })
                            }
                            rows={3}
                        />
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <IconSelector
                                label='Icon'
                                value={item.icon || ''}
                                onChange={iconName =>
                                    handleArrayChange(field.name, index, field, {
                                        ...item,
                                        icon: iconName
                                    })
                                }
                            />
                        </div>
                        <div className='space-y-3'>
                            <div className='space-y-2'>
                                <Label className='text-xs font-medium'>Icon Placement</Label>
                                <SelectMenu
                                    name={`iconPlacement-${index}`}
                                    label='Icon Placement'
                                    items={[
                                        { id: 'normal', name: 'Normal (Inside content)' },
                                        { id: 'offset_top', name: 'Top (Outside card)' },
                                        { id: 'offset_bottom', name: 'Bottom (Outside card)' },
                                        { id: 'offset_left', name: 'Left (Outside card)' },
                                        { id: 'offset_right', name: 'Right (Outside card)' }
                                    ]}
                                    value={item.iconPlacement || 'normal'}
                                    set={value =>
                                        handleArrayChange(field.name, index, field, {
                                            ...item,
                                            iconPlacement: value
                                        })
                                    }
                                />
                            </div>
                            <div className='flex items-center space-x-2'>
                                <Checkbox
                                    id={`showNumber-${index}`}
                                    checked={!!item.showNumber}
                                    className='size-4'
                                    onCheckedChange={(checked: boolean) =>
                                        handleArrayChange(field.name, index, field, {
                                            ...item,
                                            showNumber: checked
                                        })
                                    }
                                />
                                <Label htmlFor={`showNumber-${index}`} className='text-xs'>
                                    Show Number Badge
                                </Label>
                            </div>
                        </div>
                    </div>

                    <div className='space-y-2'>
                        <Label className='text-xs font-medium'>Image (optional)</Label>
                        <FormUploadImage
                            name={`card-${index}-image`}
                            className='w-[120px] h-[120px]'
                            alt={`Card ${index + 1} image`}
                            src={
                                typeof item.image === 'string' ? item.image : ''
                            }
                            onChange={file =>
                                handleArrayChange(field.name, index, field, {
                                    ...item,
                                    image: file
                                })
                            }
                        />
                    </div>

                    <div className='space-y-3'>
                        <div className='flex justify-between items-center'>
                            <Label className='text-xs font-medium'>Buttons (Max 2)</Label>
                            {((item.buttons?.length ?? 0) < 2) && (
                                <Button
                                    type='button'
                                    variant='outline'
                                    size='sm'
                                    onClick={() => {
                                        const updatedButtons = [
                                            ...(item.buttons || []),
                                            { text: '', link: '', variant: 'default', icon: '' }
                                        ]
                                        handleArrayChange(field.name, index, field, {
                                            ...item,
                                            buttons: updatedButtons
                                        })
                                    }}
                                    className='h-7 px-3 text-xs'
                                >
                                    + Add Button
                                </Button>
                            )}
                        </div>
                        {(item.buttons || []).map((button, buttonIndex) => (
                            <div
                                key={buttonIndex}
                                className='p-3 border border-border/50 rounded-lg bg-muted/20'
                            >
                                <div className='flex justify-between items-center mb-3'>
                                    <span className='text-xs font-medium text-muted-foreground'>
                                        Button {buttonIndex + 1}
                                    </span>
                                    <Button
                                        variant='ghost'
                                        size='icon'
                                        type='button'
                                        onClick={() => {
                                            const updatedButtons = [...(item.buttons || [])]
                                            updatedButtons.splice(buttonIndex, 1)
                                            handleArrayChange(field.name, index, field, {
                                                ...item,
                                                buttons: updatedButtons
                                            })
                                        }}
                                        className='h-6 w-6 text-destructive hover:text-destructive'
                                    >
                                        <IconDelete />
                                    </Button>
                                </div>
                                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                                    <div className='space-y-2'>
                                        <Label className='text-xs'>Text</Label>
                                        <Input
                                            placeholder='Button text'
                                            value={button.text || ''}
                                            onChange={e => {
                                                const updatedButtons = [...(item.buttons || [])]
                                                updatedButtons[buttonIndex] = {
                                                    ...button,
                                                    text: e.target.value
                                                }
                                                handleArrayChange(field.name, index, field, {
                                                    ...item,
                                                    buttons: updatedButtons
                                                })
                                            }}
                                        />
                                    </div>
                                    <div className='space-y-2'>
                                        <Label className='text-xs'>Link</Label>
                                        <Input
                                            placeholder='Button link'
                                            value={button.link || ''}
                                            onChange={e => {
                                                const updatedButtons = [...(item.buttons || [])]
                                                updatedButtons[buttonIndex] = {
                                                    ...button,
                                                    link: e.target.value
                                                }
                                                handleArrayChange(field.name, index, field, {
                                                    ...item,
                                                    buttons: updatedButtons
                                                })
                                            }}
                                        />
                                    </div>
                                    <div className='space-y-2'>
                                        <Label className='text-xs'>Style</Label>
                                        <SelectMenu
                                            name={`card-${index}-button-${buttonIndex}-variant`}
                                            label='Style'
                                            items={[
                                                { id: 'default', name: 'Default' },
                                                { id: 'destructive', name: 'Destructive' },
                                                { id: 'outline', name: 'Outline' },
                                                { id: 'secondary', name: 'Secondary' },
                                                { id: 'ghost', name: 'Ghost' },
                                                { id: 'link', name: 'Link' }
                                            ]}
                                            value={button.variant || 'default'}
                                            set={value => {
                                                const updatedButtons = [...(item.buttons || [])]
                                                updatedButtons[buttonIndex] = {
                                                    ...button,
                                                    variant: value
                                                }
                                                handleArrayChange(field.name, index, field, {
                                                    ...item,
                                                    buttons: updatedButtons
                                                })
                                            }}
                                        />
                                    </div>
                                    <div className='space-y-2'>
                                        <IconSelector
                                            label='Icon (optional)'
                                            value={button.icon || ''}
                                            onChange={iconName => {
                                                const updatedButtons = [...(item.buttons || [])]
                                                updatedButtons[buttonIndex] = {
                                                    ...button,
                                                    icon: iconName
                                                }
                                                handleArrayChange(field.name, index, field, {
                                                    ...item,
                                                    buttons: updatedButtons
                                                })
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

const CardArrayField: FC<FieldRendererProps> = ({
    field,
    value,
    error,
    formState = {},
    onArrayChange,
    onAddArrayItem,
    onRemoveArrayItem,
    onDuplicateArrayItem
}) => {
    const items = (value || []) as CardItem[]

    return (
        <FieldWrapper fieldName={field.name} error={error}>
            <div className='space-y-3 w-full'>
                <Label>{field.label}</Label>
                {items.map((item, index) => {
                    const cardTitle = item.title || `Card ${index + 1}`
                    const hasContent =
                        !!(item.title || item.description || item.icon || item.image)

                    return (
                        <CardArrayItem
                            key={index}
                            item={item}
                            index={index}
                            cardTitle={cardTitle}
                            hasContent={hasContent}
                            field={field}
                            handleArrayChange={onArrayChange!}
                            handleDuplicateArrayItem={onDuplicateArrayItem!}
                            handleRemoveArrayItem={onRemoveArrayItem!}
                            formState={formState}
                            isInitiallyExpanded={index === 0}
                        />
                    )
                })}

                <div className='flex items-center justify-between mt-4 p-3 bg-muted/20 rounded-lg border border-dashed border-border'>
                    <div className='flex flex-col gap-1'>
                        <span className='text-sm font-medium'>Add New Card</span>
                        <div className='text-xs text-muted-foreground'>
                            {items.length} of {field.max || '∞'} cards
                        </div>
                        {!!field.min && items.length < field.min && (
                            <p className='text-destructive text-xs'>
                                Minimum {field.min} cards required
                            </p>
                        )}
                        {field.max && items.length >= field.max && (
                            <p className='text-warning text-xs'>
                                Maximum {field.max} cards reached
                            </p>
                        )}
                    </div>
                    <Button
                        type='button'
                        onClick={() => onAddArrayItem?.(field.name, field)}
                        disabled={!!field.max && items.length >= field.max}
                        variant='outline'
                        size='sm'
                        className='shrink-0'
                    >
                        + Add Card
                    </Button>
                </div>
            </div>
        </FieldWrapper>
    )
}

export default CardArrayField
